// Normalization for the mustad.io class/item API responses, shared by the app
// (Luxstadio) and the offline tools (backtest / stats backfill) so they build
// identical `charStats` / `itemschanged` dictionaries.
//
// `safeDict` wraps a name-keyed dict so a missing key returns a frozen
// zero-filled default instead of `undefined` (which would propagate NaN through
// stat sums). It also blocks Vue's reactive-proxy probes so toRaw() doesn't
// unwrap it — harmless when used outside Vue.

export const ITEM_DEFAULT = Object.freeze({
  name: '', type: '', info: '',
  hp: 0, mp: 0, wp: 0, move: 0, jump: 0, speed: 0, pa: 0, ma: 0,
  permstatus: 0, initstatus: 0, mevade: 0, pevade: 0, wevade: 0, element: 0,
});
export const CHAR_DEFAULT = Object.freeze({
  name: '', hp: 0, mp: 0, move: 0, jump: 0, speed: 0, pa: 0, ma: 0,
  cEvPercent: 0, innates: [],
});
export const MOVE_DEFAULT = Object.freeze({ move: 0, jump: 0 });

export function safeDict(obj, defaults) {
  return new Proxy(obj, {
    get(target, key) {
      // Block Vue's reactive-proxy probes (__v_raw, __v_isReactive, …) so a
      // toRaw() on assignment doesn't strip the missing-key fallback.
      if (typeof key === 'string' && key.startsWith('__v_')) return undefined;
      if (key in target) return target[key];
      if (typeof key === 'symbol') return target[key];
      return defaults;
    },
  });
}

// Normalize one /api/items entry: lift item.stats.* to top level, default
// missing numerics to 0, rename a few fields for terseness, merge wp+absorbWp.
export function normalizeItem(apiItem) {
  const s = apiItem.stats || {};
  return {
    name:       apiItem.name || '',
    type:       apiItem.type || '',
    info:       apiItem.info || '',
    hp:         s.hp    || 0,
    mp:         s.mp    || 0,
    wp:         s.wp    || s.absorbWp || 0,
    move:       s.move  || 0,
    jump:       s.jump  || 0,
    speed:      s.speed || 0,
    pa:         s.pa    || 0,
    ma:         s.ma    || 0,
    element:    s.element          || 0,
    permstatus: s.permStatuses     || 0,
    initstatus: s.initialStatuses  || 0,
    mevade:     s.magicEvadePercent || 0,
    pevade:     s.physEvadePercent || 0,
    wevade:     s.evadePercent     || 0,
  };
}

// Same for /api/classes — lift baseStats.* to top level, default missing to 0.
export function normalizeClass(apiClass) {
  const s = apiClass.baseStats || {};
  return {
    name:       apiClass.name || '',
    hp:         s.hp    || 0,
    mp:         s.mp    || 0,
    move:       s.move  || 0,
    jump:       s.jump  || 0,
    speed:      s.speed || 0,
    pa:         s.pa    || 0,
    ma:         s.ma    || 0,
    cEvPercent: s.cEvPercent || 0,
    innates:    apiClass.innates || [],
    effect:     apiClass.effect || '', // a monster's innate elements ("Absorb Earth; Weak Fire"), read by simPrep
  };
}

// Hardcoded fallback entries for old gear no longer in the upstream /api/items
// response — without these, units in archived tournaments holding them would
// render with all-zero stats. Keys mirror the items-API stat shape.
export const LEGACY_ITEMS = [
  { name: 'Leather Vest', type: 'Armor',        stats: { hp: 40, speed: 1, move: 1 } },
  { name: 'Broad Sword',  type: 'Sword',        stats: { pa: 1 } },
  { name: 'Long Sword',   type: 'Knight Sword', stats: { wp: 5 } },
  { name: 'Ice Bow',      type: 'Bow',          stats: {} },
];

// classes array (from /api/classes) -> safeDict keyed by name+gender.
export function buildCharStats(apiClasses) {
  const classes = {};
  for (const c of apiClasses) classes[c.name + c.gender] = normalizeClass(c);
  return safeDict(classes, CHAR_DEFAULT);
}

// items array (from /api/items) -> safeDict keyed by item name, including the
// empty-slot ('') entry and the legacy fallback items.
export function buildItemsChanged(apiItems) {
  const items = {};
  for (const it of apiItems) items[it.name] = normalizeItem(it);
  items[''] = normalizeItem({ name: '', stats: {} });
  for (const it of LEGACY_ITEMS) items[it.name] = normalizeItem(it);
  return safeDict(items, ITEM_DEFAULT);
}

// abilities array (from /api/abilities or the bundled backup) -> name→info-string map.
export function buildAbilitiesInfo(apiAbilities) {
  const info = {};
  for (const a of (apiAbilities || [])) info[a.name] = a.info;
  return info;
}

// notables.json -> same dict, but with movementSkills wrapped in a safeDict so a
// missing skill returns {move:0,jump:0}. The wrap every gameData consumer repeats.
export function buildNotables(notablesJson) {
  return { ...notablesJson, movementSkills: safeDict({ ...notablesJson.movementSkills }, MOVE_DEFAULT) };
}

// One bundle of game metadata from the *.backup.json snapshots — the bootstrap
// ChampionsView / BuilderView / DynastyModal each repeated. (Luxstadio builds the
// same shape from the live mustad.io API instead.) Pass monsterSkills (the
// monster→intrinsic-skills snapshot) so the battle sim arms monster units.
export function buildGameData(classesBackup, itemsBackup, abilitiesBackup, notablesJson, monsterSkills = {}) {
  return {
    charStats: buildCharStats(classesBackup.classes),
    itemschanged: buildItemsChanged(itemsBackup.items),
    notAbles: buildNotables(notablesJson),
    abilitiesInfo: buildAbilitiesInfo(abilitiesBackup.abilities),
    monsterSkills,
  };
}
